import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import BrandSelect from "./BrandSelect";
import { Brand } from "../../types/paymentProcessor";
import { useApi } from "../../hooks/useApi";

interface OtherAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiUrl: string;
  brands: Brand[];
  refreshBrands: () => void;
  createBrandUrl?: string;
}

export default function OtherAccountDialog({ open, onClose, onSuccess, apiUrl, brands, refreshBrands, createBrandUrl }: OtherAccountDialogProps) {
  const { post } = useApi();
  const [name, setName] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState<number | ''>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [similar, setSimilar] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setName('');
    setSelectedBrandId('');
    setFormError(null);
    setSimilar(null);
    onClose();
  };

  const handleCreate = async (confirmSimilar = false) => {
    setFormError(null);
    setSimilar(null);
    setSubmitting(true);
    try {
      await post(apiUrl, { name, brandId: selectedBrandId, ...(confirmSimilar && { confirmSimilar }) });
      resetAndClose();
      await onSuccess();
    } catch (err: any) {
      if (err.data?.errors?.similar) setSimilar(err.data.errors.similar);
      else setFormError(err.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') resetAndClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle>Add Other Processor</DialogTitle>
      <DialogContent>
        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
        {similar && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            A processor with a similar name already exists: <strong>{similar}</strong>. Use that spelling, or create anyway if this is a different processor or the same one for another brand.
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => { setName(e.target.value); setFormError(null); setSimilar(null); }}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          <BrandSelect
            brands={brands}
            value={selectedBrandId}
            onChange={(brandId) => setSelectedBrandId(brandId)}
            refreshBrands={refreshBrands}
            disabled={submitting}
            createBrandUrl={createBrandUrl}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetAndClose} disabled={submitting}>Cancel</Button>
        {similar ? (
          <Button onClick={() => handleCreate(true)} variant="outlined" color="warning" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create anyway'}
          </Button>
        ) : (
          <Button
            onClick={() => handleCreate()}
            variant="contained"
            disabled={submitting || !name.trim() || !selectedBrandId}
            sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
          >
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
